/**
 * AlertsDrawer - the global Alerts drawer (Story #5351 W1-T11, Screen
 * Contract G06). Canonical route per the workbook:
 * `/operations?panel=alerts&alertId={alertId}` — URL-driven the same way
 * as JobsDrawer (G04), so a deep link/reload/handed-off link all land on
 * the same alert state.
 *
 * Reads the real, already-deployed alerts endpoint (metricsApi's `/alerts`
 * route in services/api.ts). That existing helper (`metricsApi.getAlerts`)
 * swallows every failure into a fallback that LOOKS like a genuine
 * source-complete zero (`{alerts: [], summary: {...all 0}}`) — indistin-
 * guishable from real health, which is exactly the DoD-07 violation this
 * story's StatePanel/UnavailableReason discipline exists to prevent. So
 * this component reads through `readEnvelope` (services/envelope.ts, built
 * in this same story's Stream 3) instead, which classifies a real network
 * failure as 'unreachable'/'unknown' rather than silently returning
 * known-zero. The `?environment=` param is the plain alerting scope the
 * live `/alerts` route already takes; TargetSet.accountId is left blank
 * here because it is resolved server-side and the browser never knows it
 * (contracts/targetset.ts) — decorative only, never an authorization input.
 *
 * "No fabricated unread counts" (this task's AC): every count rendered
 * here — the header total, per-severity counts — comes from the real
 * response's own `alerts`/`summary` fields. Nothing in this file is a
 * literal digit standing in for real data. The Header Bell's own badge
 * number is separately deferred to a later wave (Header.tsx's own comment)
 * and is not this component's concern.
 *
 * Deliberately NOT mounted into Header/Layout by this task — those files
 * are components/layout/*, outside this task's file ownership (same
 * handoff ScopeRibbon.tsx, HelpDrawer.tsx and JobsDrawer.tsx recorded).
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { readEnvelope } from '../../services/envelope';
import type { AlertsResponse, AlertItem } from '../../services/api';
import { useScope } from '../../contexts/ScopeContext';
import Drawer from '../ui/Drawer';
import StatePanel from '../ui/StatePanel';
import { isW1Shell } from '../../utils/w1Flag';

const PANEL_PARAM = 'panel';
const ALERT_ID_PARAM = 'alertId';
const PANEL_VALUE = 'alerts';

const SEVERITY_BADGE: Record<AlertItem['severity'], string> = {
  critical: 'bg-red-100 text-red-700',
  warning: 'bg-amber-100 text-amber-700',
  info: 'bg-sky-100 text-sky-700',
  resolved: 'bg-slate-100 text-slate-500',
};

const AlertsDrawer: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = useScope();
  const flagOn = isW1Shell();
  const isOpen = searchParams.get(PANEL_PARAM) === PANEL_VALUE;
  const environmentId = scope.legacyKey;

  const { data: envelope, isLoading } = useQuery({
    queryKey: scope.queryKey('w1-alerts-drawer', environmentId),
    queryFn: () => readEnvelope<AlertsResponse>(
      '/alerts',
      { environmentId: scope.environmentId ?? '', accountId: '', region: 'ap-southeast-2', portalId: scope.portalId ?? undefined },
      { params: { environment: environmentId, action: 'list' } },
    ),
    enabled: flagOn && isOpen && scope.resolved.kind === 'environment' && scope.resolved.environment.healthReadable === true,
    retry: false,
    refetchInterval: flagOn && isOpen ? 30000 : false,
  });

  if (!flagOn || !isOpen) return null;

  const onClose = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(PANEL_PARAM);
    next.delete(ALERT_ID_PARAM);
    setSearchParams(next);
  };

  const alerts = envelope?.data?.alerts ?? [];
  const status = scope.resolved.kind !== 'environment' || !scope.resolved.environment.healthReadable ? 'unknown' : envelope?.status;

  return (
    <Drawer onClose={onClose} ariaLabel="Alerts" maxWidthClassName="max-w-md" panelClassName="bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <h2 className="text-base font-semibold text-gray-900">
          Alerts{status === 'known' && alerts.length > 0 ? ` (${alerts.length})` : ''}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close alerts"
          className="p-1 rounded hover:bg-gray-100 text-gray-500"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading && <StatePanel state="loading" message="Loading alerts…" />}

        {!isLoading && status === 'unreachable' && (
          <StatePanel state="unreachable" reason="Could not reach the alerts source." />
        )}
        {!isLoading && status === 'unauthorised' && (
          <StatePanel state="unauthorised" reason="You do not have access to this environment's alerts." />
        )}
        {!isLoading && status === 'unknown' && (
          <StatePanel state="unknown" reason="The alerts source returned an unexpected response." />
        )}
        {!isLoading && status === 'known' && alerts.length === 0 && (
          <StatePanel state="empty" message="No active alerts." />
        )}

        {!isLoading && status === 'known' && alerts.length > 0 && (
          <ul className="space-y-2" data-testid="alerts-list">
            {alerts.map((alert) => (
              <li key={alert.id} className="border border-gray-100 rounded-md p-3">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${SEVERITY_BADGE[alert.severity]}`}>
                    {alert.severity}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{alert.name}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                <p className="text-xs text-gray-400 mt-1" title={alert.timestamp}>
                  {alert.state} · {new Date(alert.timestamp).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  );
};

export default AlertsDrawer;
